import React, { useState } from "react";
import {
  Box,
  IconButton,
  Avatar,
  CircularProgress
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { toast } from "react-toastify";
import apiService from "../../../service/Api";

interface EditAvatarProps {
  fetchMember: () => void;
  initialImage: string | undefined;
  pilot: {
    id: number;
    email: string;
    name: string;
  };
};

export const EditAvatar: React.FC<EditAvatarProps> = ({
  fetchMember,
  initialImage,
  pilot
}) => {
  const [avatarLoading, setAvatarLoading] = useState<boolean>(false);

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPG, JPEG, or PNG).');
        return;
      }
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('File size too large. Maximum size is 5MB.');
        return;
      }
      handleAvatarUpdate(file);
    }
  };

  const handleAvatarUpdate = async (image: File) => {
    setAvatarLoading(true);
    try {
      const fd = new FormData();

      fd.append('email', pilot.email);
      fd.append('profile_picture', image);
      
      await apiService.patch(`/api/authentication/users/${pilot.id}/`, fd, false);

      fetchMember();

      toast.success(`You have successfully updated ${pilot.name}'s avatar!`);
    } catch (err) {
      console.log(err, 'Pilot Avatar Updating Error');
      toast.error(`Failed to update ${pilot.name}'s avatar!`);
    } finally {
      setAvatarLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          width: 80,
          height: 80,
          borderRadius: '50%',
          overflow: 'hidden',
          cursor: 'pointer',
          '&:hover .overlay': {
            opacity: 1
          }
        }}
      >
        <Avatar
          src={initialImage}
          sx={{
            width: 80,
            height: 80,
            '&:hover': {

            },
            borderRadius: "50%"
          }}
        />
        <Box
          className={'overlay'}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: '#00000070',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
        >
          <label htmlFor="upload-avatar">
            <IconButton
              color="primary"
              sx={{ color: 'white' }}
              component="span"
            >
              {!avatarLoading ?
                <Edit /> : <CircularProgress color={'inherit'} size={12} />
              }
            </IconButton>
          </label>
          <input
            accept="image/png,image/jpeg,image/jpg"
            id={'upload-avatar'}
            style={{ display: 'none' }}
            type="file"
            onChange={handleAvatarChange}
          />
        </Box>
      </Box>
    </Box>
  );
};
